import React, { useEffect, useRef } from "react";
import { WhatWeDoSectionStyled } from "./WhatWeDoSectionStyled";
import WhatWeDoItems from "./WhatWeDoItems";
import { whatWeDoItems } from "./constants";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// IT professionals working - Unsplash
const WhatWeDoImage = "https://images.unsplash.com/photo-1531482615713-2afd69097998?w=800&q=80";

gsap.registerPlugin(ScrollTrigger);

const WhatWeDoSection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);
  const itemsRef = useRef<HTMLDivElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    const header = headerRef.current;
    const image = imageRef.current;
    const items = itemsRef.current;
    const stats = statsRef.current;

    if (!section || !header || !image || !items) return;

    const ctx = gsap.context(() => {
      // Header animation
      gsap.fromTo(
        header.querySelectorAll(".sectionLabel, h2, p"),
        { opacity: 0, y: 40 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          stagger: 0.15,
          ease: "power3.out",
          scrollTrigger: {
            trigger: section,
            start: "top 80%",
            toggleActions: "play none none reverse",
          },
        }
      );

      // Image animation
      gsap.fromTo(
        image.querySelector(".imageFrame"),
        { opacity: 0, x: -80, scale: 0.9 },
        {
          opacity: 1,
          x: 0,
          scale: 1,
          duration: 1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: image,
            start: "top 80%",
            toggleActions: "play none none reverse",
          },
        }
      );

      // Stats box animation
      if (stats) {
        gsap.fromTo(
          stats,
          { opacity: 0, y: 30, scale: 0.9 },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 0.6,
            delay: 0.4,
            ease: "back.out(1.7)",
            scrollTrigger: {
              trigger: image,
              start: "top 75%",
              toggleActions: "play none none reverse",
            },
          }
        );
      }

      // Items stagger animation
      const itemElements = items.children;
      gsap.fromTo(
        itemElements,
        { opacity: 0, x: 50 },
        {
          opacity: 1,
          x: 0,
          duration: 0.6,
          stagger: 0.12,
          ease: "power3.out",
          scrollTrigger: {
            trigger: items,
            start: "top 80%",
            toggleActions: "play none none reverse",
          },
        }
      );
    }, section);

    return () => ctx.revert();
  }, []);

  return (
    <WhatWeDoSectionStyled ref={sectionRef}>
      {/* Rotating badge decoration */}
      <div className="rotatingBadge">
        <svg viewBox="0 0 100 100">
          <defs>
            <path id="circle" d="M 50, 50 m -37, 0 a 37,37 0 1,1 74,0 a 37,37 0 1,1 -74,0" />
          </defs>
          <text>
            <textPath href="#circle">
              • TECH EXCELLENCE • SERVICE QUALITY • TRUSTED PARTNER
            </textPath>
          </text>
        </svg>
      </div>

      <div className="whatWeDoSectionContainer">
        <div className="sectionHeader" ref={headerRef}>
          <div className="headerLeft">
            <div className="sectionLabel">
              <span className="line" />
              <span>About Us</span>
            </div>
            <h2>
              What <span className="outline">We</span> Are
            </h2>
          </div>
          <div className="headerRight">
            <p>
              Leading IT solutions provider in Kuwait, delivering excellence in
              technology services and products for over two decades.
            </p>
          </div>
        </div>

        <div className="whatweDoWrapper">
          <div className="imageWrapper" ref={imageRef}>
            <div className="imageFrame">
              <img src={WhatWeDoImage} alt="What We Do" />
            </div>
            <div className="statsBox" ref={statsRef}>
              <div className="number">25+</div>
              <div className="label">Years Experience</div>
            </div>
          </div>

          <div className="itemsWrapper" ref={itemsRef}>
            {whatWeDoItems?.map((items, index) => {
              return (
                <WhatWeDoItems
                  title={items.title}
                  description={items.description}
                  ImageIcon={items.image}
                  key={items.id}
                  index={index}
                />
              );
            })}
          </div>
        </div>
      </div>
    </WhatWeDoSectionStyled>
  );
};

export default WhatWeDoSection;
